// The background remover on /tools/background-remover.
//
// The model does the expensive part once per image. Changing the backdrop or
// the output format afterwards only redraws the cut-out that is already in
// memory, so those controls answer instantly rather than costing another run.

import { removeBackground, MODELS } from './tools/segmenter.js';
import { explainLoadError, RUNTIME_BYTES } from './tools/onnx.js';
import { decode, sizeOf, encode, renameExtension, saveBlob, formatBytes, canEncode } from './tools/images.js';
import { wirePicker, blockStrayDrops, initColourPicker } from './tools/picker.js';
import { wireCopy } from './tools/copy.js';
import { wireOffline } from './tools/offline.js';
import { wireAccountSave } from './tools/account.js';

// PNG first: it is the only one of the three that keeps a transparent backdrop,
// which is the point of the tool.
const FORMATS = [
    { mime: 'image/png',  label: 'PNG',  extension: 'png',  transparent: true,  quality: undefined },
    { mime: 'image/webp', label: 'WebP', extension: 'webp', transparent: true,  quality: 0.92 },
    { mime: 'image/jpeg', label: 'JPEG', extension: 'jpg',  transparent: false, quality: 0.92 },
];

const el = id => document.getElementById(id);

const dom = {
    zone: el('bgZone'),
    input: el('bgInput'),
    empty: el('bgEmpty'),
    workspace: el('bgWorkspace'),
    model: el('bgModel'),
    modelNote: el('bgModelNote'),
    backdrop: el('bgBackdrop'),
    colourRow: el('bgColourRow'),
    colour: el('bgColour'),
    format: el('bgFormat'),
    status: el('bgStatus'),
    statusText: el('bgStatusText'),
    progress: el('bgProgress'),
    progressBar: el('bgProgressBar'),
    error: el('bgError'),
    errorText: el('bgErrorText'),
    retry: el('bgRetry'),
    live: el('bgLive'),
    preview: el('bgPreview'),
    image: el('bgImage'),
    compare: el('bgCompare'),
    meta: el('bgMeta'),
    download: el('bgDownload'),
    reset: el('bgReset'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
};

const state = {
    file: null,
    source: null,      // The decoded original, kept for the comparison view.
    sourceUrl: null,
    cutout: null,      // Canvas with the subject on transparent pixels.
    output: null,      // { blob, name, url } for the current settings.
    running: false,
};

function format() {
    return FORMATS.find(f => f.mime === dom.format.value) || FORMATS[0];
}

function backdrop() {
    // JPEG has no alpha channel to be transparent in. Rather than letting the
    // encoder decide what shows through, which is black in some browsers and
    // white in others, the colour is made explicit and the control says so.
    if (!format().transparent) return dom.colour.value;
    return dom.backdrop.value === 'colour' ? dom.colour.value : null;
}

function syncControls() {
    const transparentPossible = format().transparent;
    dom.backdrop.disabled = !transparentPossible;
    dom.colourRow.hidden = transparentPossible && dom.backdrop.value !== 'colour';

    const model = MODELS.find(m => m.id === dom.model.value) || MODELS[0];
    dom.modelNote.textContent = `${model.note}. ${formatBytes(model.bytes)} model, plus the ${formatBytes(RUNTIME_BYTES)} runtime the first time.`;
}

function setStatus(text, fraction) {
    // The live region hears each new stage once; the bar's percentages are
    // left to the progressbar role, which a screen reader can ask for.
    if (text && text !== dom.statusText.textContent) dom.live.textContent = text;
    dom.status.hidden = !text;
    dom.statusText.textContent = text || '';
    dom.progress.hidden = fraction === undefined;
    if (fraction !== undefined) {
        const percent = Math.round(fraction * 100);
        dom.progressBar.style.width = `${percent}%`;
        dom.progress.setAttribute('aria-valuenow', String(percent));
    }
}

/**
 * Show a problem, or clear it with an empty message.
 *
 * @param {string} message
 * @param {object} [options]
 * @param {boolean} [options.retry]   Offer "Try again", for a download that failed.
 * @param {boolean} [options.notice]  A refusal rather than a failure, shown in amber.
 */
function setError(message, { retry = false, notice = false } = {}) {
    dom.error.hidden = !message;
    dom.errorText.textContent = message || '';
    dom.retry.hidden = !(message && retry);
    dom.error.classList.toggle('bg-red-50', !notice);
    dom.error.classList.toggle('dark:bg-red-950/30', !notice);
    dom.error.classList.toggle('border-red-200/50', !notice);
    dom.error.classList.toggle('dark:border-red-800/50', !notice);
    dom.error.classList.toggle('text-red-700', !notice);
    dom.error.classList.toggle('dark:text-red-300', !notice);
    dom.error.classList.toggle('bg-amber-50', notice);
    dom.error.classList.toggle('dark:bg-amber-950/30', notice);
    dom.error.classList.toggle('border-amber-200/50', notice);
    dom.error.classList.toggle('dark:border-amber-800/50', notice);
    dom.error.classList.toggle('text-amber-800', notice);
    dom.error.classList.toggle('dark:text-amber-200', notice);
}

function releaseOutput() {
    if (state.output) URL.revokeObjectURL(state.output.url);
    state.output = null;
}

/** Redraw the cut-out over the chosen backdrop and encode it. */
async function present() {
    if (!state.cutout) return;

    const chosen = format();
    const colour = backdrop();

    const canvas = document.createElement('canvas');
    canvas.width = state.cutout.width;
    canvas.height = state.cutout.height;

    const ctx = canvas.getContext('2d');
    if (colour) {
        ctx.fillStyle = colour;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(state.cutout, 0, 0);

    const blob = await encode(canvas, chosen.mime, chosen.quality);
    releaseOutput();
    state.output = {
        blob,
        name: renameExtension(state.file.name, chosen.extension),
        url: URL.createObjectURL(blob),
    };

    // The checkerboard behind the preview only makes sense while something is
    // actually see-through.
    dom.preview.classList.toggle('is-transparent', !colour);
    if (!dom.compare.getAttribute('aria-pressed') || dom.compare.getAttribute('aria-pressed') === 'false') {
        dom.image.src = state.output.url;
    }

    dom.download.disabled = false;
    dom.meta.textContent = `${canvas.width} x ${canvas.height} - ${formatBytes(blob.size)} - ${state.output.name}`;
}

async function process() {
    if (!state.source || state.running) return;

    state.running = true;
    dom.download.disabled = true;
    setError('');

    try {
        const model = MODELS.find(m => m.id === dom.model.value) || MODELS[0];
        state.cutout = await removeBackground(state.source, {
            model,
            onDownload: fraction => setStatus(`Downloading the ${model.label.toLowerCase()} model`, fraction),
            onStage: text => setStatus(text),
        });
        setStatus('Rendering');
        await present();
        setStatus('');
    } catch (err) {
        setStatus('');
        setError(explainLoadError(err), { retry: true });
    } finally {
        state.running = false;
    }
}

function reset() {
    releaseOutput();
    if (state.sourceUrl) URL.revokeObjectURL(state.sourceUrl);
    if (state.source?.close) state.source.close();

    state.file = null;
    state.source = null;
    state.sourceUrl = null;
    state.cutout = null;

    dom.image.removeAttribute('src');
    dom.workspace.hidden = true;
    dom.empty.hidden = false;
    dom.compare.setAttribute('aria-pressed', 'false');
    dom.download.disabled = true;
    dom.meta.textContent = '';
    setStatus('');
    setError('');
}

async function open([file]) {
    if (!file) return;
    reset();

    state.file = file;
    try {
        state.source = await decode(file);
    } catch (err) {
        setError(err.message || 'Could not read this image');
        return;
    }

    // Shown before the model has said anything, so the picture appears at once
    // and the wait happens with something on screen.
    state.sourceUrl = URL.createObjectURL(file);
    dom.image.src = state.sourceUrl;
    dom.preview.classList.remove('is-transparent');

    const { width, height } = sizeOf(state.source);
    dom.meta.textContent = `${width} x ${height} - ${formatBytes(file.size)}`;

    dom.empty.hidden = true;
    dom.workspace.hidden = false;

    process();
}

function toggleCompare() {
    const showingOriginal = dom.compare.getAttribute('aria-pressed') === 'true';
    const next = !showingOriginal;
    dom.compare.setAttribute('aria-pressed', String(next));

    if (next) {
        dom.image.src = state.sourceUrl;
        dom.preview.classList.remove('is-transparent');
    } else if (state.output) {
        dom.image.src = state.output.url;
        dom.preview.classList.toggle('is-transparent', !backdrop());
    }
}

async function dropUnsupportedFormats() {
    for (const option of Array.from(dom.format.options)) {
        if (!(await canEncode(option.value))) option.remove();
    }
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    initColourPicker();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: false, onFiles: open });

    dropUnsupportedFormats().then(syncControls);

    dom.model.addEventListener('change', () => {
        syncControls();
        // A different model is a different cut-out, so this one has to run again.
        process();
    });

    for (const control of [dom.backdrop, dom.format, dom.colour]) {
        control.addEventListener('input', () => {
            syncControls();
            present().catch(err => setError(err.message || 'Could not render the result'));
        });
    }

    dom.compare.addEventListener('click', toggleCompare);
    dom.retry.addEventListener('click', () => { setError(''); process(); });
    dom.download.addEventListener('click', () => {
        if (state.output) saveBlob(state.output.blob, state.output.name);
    });
    dom.reset.addEventListener('click', reset);
    const recheckOffline = wireOffline({
        root: document.getElementById('bgOffline'),
        button: document.getElementById('bgOfflineButton'),
        note: document.getElementById('bgOfflineNote'),
        model: () => MODELS.find(m => m.id === dom.model.value) || MODELS[0],
    });
    dom.model.addEventListener('change', recheckOffline);
    wireCopy({ button: document.getElementById('bgCopy'), mirror: dom.download, collect: () => state.output });

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        // Whatever is on screen right now, which present() has already encoded
        // in the chosen format and over the chosen backdrop.
        collect: () => state.output ? [{ blob: state.output.blob, name: state.output.name }] : [],
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
